import { Fragment, useState } from 'react';
import { BossIcon } from '../bosses/BossIcon';
import { CollapsibleSection } from '../goals/CollapsibleSection';
import { CharacterPitchedList } from './CharacterPitchedList';
import {
  bossNameFor,
  groupPitchedItemsByBoss,
  luckiestCharacter,
  orderBrilliantItems,
  perBossDropTotals,
  pitchedTotals,
  rosterDropTotal,
} from '../../lib/pitchedGrouping';
import { avatarProxyPath } from '../../lib/roster';
import type { PitchedDropCountDto } from '../../lib/pitched';
import type { BossDto, PitchedItemDto } from '../../lib/reference';

export interface PitchedOverviewCharacter {
  characterId: number;
  accountId: number;
  name: string;
  avatarUrl: string | null;
  obtainedSlugs: string[];
  /** REV17 */
  dropCounts: PitchedDropCountDto[];
}

interface PitchedOverviewProps {
  characters: PitchedOverviewCharacter[];
  items: PitchedItemDto[];
  bossCatalog: BossDto[];
  pendingKey: string | null;
  onToggle: (character: PitchedOverviewCharacter, item: PitchedItemDto, obtained: boolean) => void;
  /** REV17 */
  pendingDropKey: string | null;
  onDropCountChange: (character: PitchedOverviewCharacter, bossSlug: string, count: number) => void;
}

/**
 * REV17 — the Luckiest-character avatar, same fallback-to-initial idiom as `CharacterTabStrip`'s
 * `TabAvatar` (kept local rather than extracted since the two aren't otherwise shared).
 */
function LuckiestAvatar({ src, name }: { src: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-elevated-hover text-xs font-semibold text-text-faint">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{name.trim().slice(0, 1).toUpperCase() || '?'}</span>
      )}
    </span>
  );
}

/**
 * Account-wide "Overview" tab (REV6 spec): a characters × items matrix on desktop/tablet, the same
 * "rows = catalog entries, columns = characters" orientation as `BossMatrixGrid` — collapsed to one
 * card per character on mobile (each card *is* that character's own `CharacterPitchedList`, in a
 * closed-by-default `CollapsibleSection` so the page doesn't dump every item for every character at
 * once on a small screen).
 */
export function PitchedOverview({
  characters,
  items,
  bossCatalog,
  pendingKey,
  onToggle,
  pendingDropKey,
  onDropCountChange,
}: PitchedOverviewProps) {
  const groups = groupPitchedItemsByBoss(items, bossCatalog);
  const brilliantItems = orderBrilliantItems(items, bossCatalog);

  if (characters.length === 0) {
    return <p className="text-sm text-text-muted">Add a character under Characters to start tracking Pitched items.</p>;
  }

  // REV17 — roster total, per-boss sums, and the "Luckiest character" fun stat. `characters` is
  // already in roster order (Pitched.tsx maps it straight off the account's character list), so
  // `luckiestCharacter`'s tie-break-by-first-seen falls out of that order for free.
  const rosterTotal = rosterDropTotal(characters);
  const bossTotals = perBossDropTotals(characters, bossCatalog);
  const luckiest = luckiestCharacter(characters);

  return (
    <div className="space-y-4">
      {rosterTotal > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
              Roster lifetime drops
            </p>
            <p className="mt-0.5 text-lg font-semibold text-text">{rosterTotal}</p>
          </div>

          {bossTotals.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {bossTotals.map((bossTotal) => (
                <span key={bossTotal.bossSlug} className="flex items-center gap-1.5 text-sm text-text-muted">
                  <BossIcon slug={bossTotal.bossSlug} name={bossTotal.bossName} />
                  {bossTotal.total}
                </span>
              ))}
            </div>
          )}

          {luckiest && (
            <div className="ml-auto flex items-center gap-2">
              <LuckiestAvatar
                src={
                  characters.find((c) => c.characterId === luckiest.characterId)?.avatarUrl != null
                    ? avatarProxyPath(
                        characters.find((c) => c.characterId === luckiest.characterId)!.accountId,
                        luckiest.characterId,
                      )
                    : null
                }
                name={luckiest.name}
              />
              <p className="text-sm font-medium text-text">
                Luckiest: {luckiest.name} — {luckiest.total} {luckiest.total === 1 ? 'drop' : 'drops'}
              </p>
            </div>
          )}
        </div>
      )}

      <div role="region" aria-label="Pitched matrix (grid)" className="hidden overflow-x-auto rounded-xl border border-border bg-bg-elevated md:block">
        <table className="w-full min-w-[700px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-bg-elevated-hover">
              <th className="sticky left-0 z-10 min-w-[12rem] bg-bg-elevated-hover px-3 py-2 text-left font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
                Item
              </th>
              {characters.map((character) => (
                <th key={character.characterId} className="min-w-[6rem] px-2 py-2 text-center">
                  <p className="truncate text-sm font-semibold text-text">{character.name}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.bossSlug}>
                <tr className="bg-bg-elevated-hover">
                  <td
                    colSpan={characters.length + 1}
                    className="sticky left-0 bg-bg-elevated-hover px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-text-faint"
                  >
                    <span className="flex items-center gap-2">
                      <BossIcon slug={group.bossSlug} name={group.bossName} />
                      {group.bossName}
                    </span>
                  </td>
                </tr>
                {group.items.map((item) => (
                  <PitchedMatrixRow
                    key={item.slug}
                    item={item}
                    characters={characters}
                    pendingKey={pendingKey}
                    onToggle={onToggle}
                  />
                ))}
              </Fragment>
            ))}
            {brilliantItems.length > 0 && (
              <Fragment>
                <tr className="bg-bg-elevated-hover">
                  <td
                    colSpan={characters.length + 1}
                    className="sticky left-0 bg-bg-elevated-hover px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-text-faint"
                  >
                    Brilliant Boss Set
                  </td>
                </tr>
                {brilliantItems.map((item) => (
                  <PitchedMatrixRow
                    key={item.slug}
                    item={item}
                    sourceBossName={bossNameFor(bossCatalog, item.sourceBossSlug)}
                    characters={characters}
                    pendingKey={pendingKey}
                    onToggle={onToggle}
                  />
                ))}
              </Fragment>
            )}
          </tbody>
        </table>
      </div>

      <div role="region" aria-label="Pitched matrix (mobile)" className="space-y-3 md:hidden">
        {characters.map((character) => {
          const totals = pitchedTotals(items, character.obtainedSlugs);
          return (
            <CollapsibleSection
              key={character.characterId}
              title={character.name}
              subtitle={`${totals.pitchedObtained}/${totals.pitchedTotal} Pitched · ${totals.brilliantObtained}/${totals.brilliantTotal} Brilliant`}
              defaultOpen={false}
            >
              <CharacterPitchedList
                characterName={character.name}
                characterId={character.characterId}
                items={items}
                bossCatalog={bossCatalog}
                obtainedSlugs={character.obtainedSlugs}
                pendingSlug={
                  pendingKey?.startsWith(`${character.characterId}:`) ? (pendingKey.split(':')[1] ?? null) : null
                }
                onToggle={(item, obtained) => onToggle(character, item, obtained)}
                dropCounts={character.dropCounts}
                pendingDropBossSlug={
                  pendingDropKey?.startsWith(`${character.characterId}:`)
                    ? (pendingDropKey.split(':')[1] ?? null)
                    : null
                }
                onDropCountChange={(bossSlug, count) => onDropCountChange(character, bossSlug, count)}
              />
            </CollapsibleSection>
          );
        })}
      </div>
    </div>
  );
}

function PitchedMatrixRow({
  item,
  sourceBossName,
  characters,
  pendingKey,
  onToggle,
}: {
  item: PitchedItemDto;
  sourceBossName?: string;
  characters: PitchedOverviewCharacter[];
  pendingKey: string | null;
  onToggle: (character: PitchedOverviewCharacter, item: PitchedItemDto, obtained: boolean) => void;
}) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-bg-elevated-hover">
      <td className="sticky left-0 z-10 min-w-[12rem] bg-bg-elevated px-3 py-1.5 align-middle">
        <p className={['truncate text-sm font-medium text-text', item.retired ? 'opacity-70' : ''].join(' ')}>
          {item.name}
        </p>
        <p className="truncate text-xs text-text-faint">
          {item.equipSlot}
          {sourceBossName ? ` · ${sourceBossName}` : ''}
          {item.retired && ' · retired'}
          {!item.verified && ' · unverified'}
        </p>
      </td>
      {characters.map((character) => {
        const obtained = character.obtainedSlugs.includes(item.slug);
        const pending = pendingKey === `${character.characterId}:${item.slug}`;
        return (
          <td key={character.characterId} className="px-2 py-1 text-center">
            <button
              type="button"
              disabled={pending}
              aria-pressed={obtained}
              aria-label={`${item.name} obtained for ${character.name}`}
              onClick={() => onToggle(character, item, !obtained)}
              className={[
                'inline-flex min-h-tap min-w-tap items-center justify-center rounded-full px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                obtained
                  ? 'bg-accent-dim text-accent-strong'
                  : 'border border-border-strong bg-bg-elevated text-text-faint hover:bg-bg-elevated-hover',
              ].join(' ')}
            >
              {obtained ? '✓' : '—'}
            </button>
          </td>
        );
      })}
    </tr>
  );
}
